import axios from 'axios';
import { registerDevice } from '@/utilities/registerDevice';
import { getRefreshToken, setRefreshToken, clearRefreshToken } from '@/utilities/refreshTokenStorage';
import { readSessionToken, writeSessionToken } from '@/utilities/sessionTokenStorage';
import {
  isHmsSubscriptionInactiveError,
  HMS_SUBSCRIPTION_INACTIVE_EVENT,
} from '@/utilities/errorHelper';

// Get environment variables
const SMS_BASE_URL = process.env.VUE_APP_SMS_BASE_URL || 'https://sms.nizosoft.com';
const HMS_BASE_URL = process.env.VUE_APP_HMS_BASE_URL || 'https://hms.nizosoft.com';
const UPLOAD_BASE_URL = process.env.VUE_APP_UPLOAD_BASE_URL || 'https://upload.nizosoft.com';
const EPAY_BASE_URL = process.env.VUE_APP_EPAY_BASE_URL || 'https://epay.nizosoft.com';
const API_STAGE = process.env.VUE_APP_API_STAGE || 'betav1';

// Build full base URLs
const SMS_API_BASE = `${SMS_BASE_URL}/${API_STAGE}`;
const HMS_API_BASE = `${HMS_BASE_URL}/${API_STAGE}`;
const UPLOAD_API_BASE = `${UPLOAD_BASE_URL}/${API_STAGE}`;
const EPAY_API_BASE = `${EPAY_BASE_URL}/${API_STAGE}`;

// Create Axios instances for SMS and HMS APIs
// smsApiWithDeviceToken - for authentication endpoints that require device token
const smsApiWithDeviceToken = axios.create({
  baseURL: SMS_API_BASE,
});

// smsApiWithSessionToken - for SMS endpoints that require session token (after login)
const smsApiWithSessionToken = axios.create({
  baseURL: SMS_API_BASE,
});

const hmsApi = axios.create({
  baseURL: HMS_API_BASE,
});

const uploadApi = axios.create({
  baseURL: UPLOAD_API_BASE,
});

const epayApi = axios.create({
  baseURL: EPAY_API_BASE,
});

let refreshPromise = null;

const getSessionToken = () => readSessionToken();

// Request interceptor for SMS API with Device Token (for auth endpoints)
smsApiWithDeviceToken.interceptors.request.use((config) => {
  const token = localStorage.getItem('DEVICE_REG_TOKEN');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Request interceptor for SMS API with Session Token (for post-login endpoints)
smsApiWithSessionToken.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Request interceptor for HMS API (uses session token)
hmsApi.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

epayApi.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

hmsApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isHmsSubscriptionInactiveError(error) && typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(HMS_SUBSCRIPTION_INACTIVE_EVENT, {
          detail: {
            reason: error.response?.data?.reason || 'Subscription not active',
          },
        })
      );
    }
    return Promise.reject(error);
  }
);

const isJwtExpiredSessionError = (error) => {
  const status = error.response?.status;
  const data = error.response?.data;
  const errorCode = Number(data?.errorcode);
  const reason = String(data?.reason || '').toLowerCase();
  return status === 401 && errorCode === 303 && reason.includes('jwt expired');
};

const isRefreshTokenJwtExpiredError = (error) => {
  const data = error.response?.data;
  if (!data) {
    return false;
  }
  const errorCode = Number(data.errorcode);
  const reason = String(data.reason || '').toLowerCase();
  return errorCode === 311 && reason.includes('jwt expired');
};

const isRefreshTokenRevokedError = (error) => {
  const data = error.response?.data;
  if (!data) {
    return false;
  }
  const errorCode = Number(data.errorcode);
  const reason = String(data.reason || '').toLowerCase();
  return errorCode === 3229 || reason.includes('refresh token revoked');
};

/** Pick rotated refresh token from various API shapes (camelCase / snake_case). */
const pickRotatedRefreshToken = (data) => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const candidates = [
    data.refreshToken,
    data.refresh_token,
    data.newRefreshToken,
    data.new_refresh_token,
    data.refreshtoken,
  ];
  const found = candidates.find((t) => typeof t === 'string' && t.length > 0);
  return found || null;
};

const performSessionExpiredLogout = async () => {
  if (typeof window === 'undefined') {
    return;
  }
  clearRefreshToken();
  const deviceToken = localStorage.getItem('DEVICE_REG_TOKEN');
  try {
    localStorage.clear();
  } catch (e) {
    console.error('localStorage.clear failed:', e);
  }
  if (deviceToken) {
    localStorage.setItem('DEVICE_REG_TOKEN', deviceToken);
  }
  // Dynamic import avoids circular dependency (router → components → api).
  const { default: routerInstance } = await import('@/router');
  routerInstance.replace({ name: 'Login' }).catch(() => {});
};

const requestSessionRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('Missing refresh token');
  }

  // Use plain axios (GET) so smsApiWithDeviceToken's interceptor does not overwrite
  // Authorization with the device registration token.
  let response;
  try {
    response = await axios.get(`${SMS_API_BASE}/provider/v1/refresh`, {
      params: { refreshToken },
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    });
  } catch (error) {
    if (isRefreshTokenJwtExpiredError(error) || isRefreshTokenRevokedError(error)) {
      await performSessionExpiredLogout();
    }
    throw error;
  }

  const refreshedData = response?.data || {};
  const newSessionToken = refreshedData?.success;
  const newRefreshToken = pickRotatedRefreshToken(refreshedData);

  if (!newSessionToken) {
    throw new Error('Invalid refresh response: missing session token');
  }

  // Single-use refresh: persist new refresh token before any other work uses storage.
  if (!newRefreshToken) {
    clearRefreshToken();
    throw new Error('Invalid refresh response: missing rotated refresh token');
  }

  setRefreshToken(newRefreshToken);
  writeSessionToken(newSessionToken);

  return newSessionToken;
};

const refreshSessionToken = async () => {
  if (!refreshPromise) {
    refreshPromise = requestSessionRefresh().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

const withSessionRefresh = (apiInstance) => {
  apiInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        isJwtExpiredSessionError(error)
        && originalRequest
        && !originalRequest._retry
        && !originalRequest.skipSessionRefresh
      ) {
        originalRequest._retry = true;

        try {
          const newSessionToken = await refreshSessionToken();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newSessionToken}`;
          return apiInstance(originalRequest);
        } catch (refreshError) {
          clearRefreshToken();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

withSessionRefresh(smsApiWithSessionToken);
withSessionRefresh(hmsApi);
withSessionRefresh(epayApi);

// Response interceptor for SMS API with Device Token (with token refresh)
smsApiWithDeviceToken.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const status = error.response?.status;
    const errorCode = error.response?.data?.errorcode;

    // Check if the error is due to expired token
    const isJwtExpired = status === 401 || errorCode === 303;

    if (isJwtExpired && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Get new token by re-registering the device
        await registerDevice();
        const newToken = localStorage.getItem('DEVICE_REG_TOKEN');
        if (!newToken) {
          throw new Error('Failed to refresh token');
        }

        // Update token in original request
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Retry the original request
        return smsApiWithDeviceToken(originalRequest);
      } catch (e) {
        console.error('Token refresh failed:', e);
        return Promise.reject(e);
      }
    }

    // If it's not a token issue, reject normally
    return Promise.reject(error);
  }
);


// API Service Object - Centralized API endpoints
const apiService = {
  // ============ SMS API Endpoints ============
  
  // Authentication & User Management (Requires Device Token)
  login: (email, password) => {
    return smsApiWithDeviceToken.get('/provider/v1/login', {
      params: { email, password }
    });
  },

  signup: (payload) => {
    return smsApiWithDeviceToken.post('/provider/v1/signup', payload);
  },

  sendEmailOtp: (email) => {
    return smsApiWithDeviceToken.get('/provider/v1/sendemailotp', {
      params: { email }
    });
  },

  verifyEmailOtp: (email, otp) => {
    return smsApiWithDeviceToken.get('/provider/v1/verifyemailotp', {
      params: { email, otp }
    });
  },

  verifyEmailMobileOtp: (email, otp, mobileno, otpMobile) => {
    return smsApiWithDeviceToken.get('/provider/v1/verifyemailmobileotp', {
      params: { email, emailOtp: otp, mobileno, mobileOtp: otpMobile }
    });
  },

  resetPassword: (email, otp, newPassword) => {
    return smsApiWithDeviceToken.get('/provider/v1/resetpassword', {
      params: { email, otp, newpassword: newPassword }
    });
  },

  logout: () => {
    // Allow session refresh on jwt expired so logout can succeed after token rotation.
    return smsApiWithSessionToken.get('/provider/v1/logout');
  },

  // Provider Admin (Requires Session Token)
  getProviderAdmin: () => {
    return smsApiWithSessionToken.get('/provider/v1/provideradmin');
  },

  updateProviderAdmin: (payload) => {
    return smsApiWithSessionToken.put('/provider/v1/provideradmin', payload);
  },

  getSubscription: () => {
    return smsApiWithSessionToken.get('/provider/v1/subscription');
  },

  getVisiblePlans: () => {
    return smsApiWithSessionToken.get('/provider/v1/plan', {
      params: { planvisibility: 'YES' },
    });
  },

  refreshSession: () => {
    return refreshSessionToken();
  },

  initEsewaPayment: (payload) => {
    const form = new URLSearchParams();
    form.set('devicetype', payload.devicetype || 'WEB');
    form.set('amount', String(payload.amount || ''));
    form.set('currency', payload.currency || 'NPR');
    form.set('transactionpurpose', payload.transactionpurpose || 'SUBSCRIPTION');
    form.set('transactionmode', payload.transactionmode || 'WA');
    form.set('planid', payload.planid || '');
    form.set('availabilityid', payload.availabilityid || '');
    if (payload.successUrl) {
      form.set('successUrl', String(payload.successUrl));
    }
    if (payload.failureUrl) {
      form.set('failureUrl', String(payload.failureUrl));
    }

    return epayApi.post('/subscriber/v1/payment/init/esewa', form, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },

  getPaymentGateways: () => {
    return epayApi.get('/product/v1/gateway');
  },

  esewaCallback: (referenceid, referencedata) => {
    const form = new URLSearchParams();
    form.set('referenceid', String(referenceid || ''));
    form.set('referencedata', JSON.stringify(referencedata || {}));
    return epayApi.post('/callback/v1/esewa', form, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
    });
  },

  // Object Management (Requires Session Token)
  getObjectList: () => {
    return hmsApi.get('/provider/v1/objectlist');
  },

  // Sub Users (Requires Session Token)
  getSubUsers: () => {
    return smsApiWithSessionToken.get('/provider/v1/provider/subuser');
  },

  createSubUser: (payload) => {
    return smsApiWithSessionToken.post('/provider/v1/provider/subuser', payload);
  },

  updateSubUser: (providerAdminId, payload) => {
    return smsApiWithSessionToken.put(`/provider/v1/provideradmin/${providerAdminId}`, payload);
  },

  // Device Registration
  registerDevice: (url, payload) => {
    return axios.post(url, payload, {
      headers: { 'Content-Type': 'text/plain' }
    });
  },

  // ============ HMS API Endpoints ============

  // Object Management
  getObject: (objectId) => {
    return hmsApi.get(`/provider/v1/object/${objectId}`);
  },

  createObject: (payload) => {
    return hmsApi.post('/provider/v1/object', payload);
  },

  updateObject: (objectId, payload) => {
    return hmsApi.put(`/provider/v1/object/${objectId}`, payload);
  },

  // Object Space
  getObjectSpace: (params) => {
    return hmsApi.get('/provider/v1/objectspace', { params });
  },

  getObjectSpaceById: (spaceId) => {
    return hmsApi.get(`/provider/v1/objectspace/${spaceId}`);
  },

  createObjectSpace: (payload) => {
    return hmsApi.post('/provider/v1/objectspace', payload);
  },

  updateObjectSpace: (spaceId, payload) => {
    return hmsApi.put(`/provider/v1/objectspace/${spaceId}`, payload);
  },

  // Space Room
  getSpaceRooms: (params) => {
    return hmsApi.get('/provider/v1/spaceroom', { params });
  },

  deleteSpaceRoom: (params) => {
    return hmsApi.delete('/provider/v1/spaceroom', { params });
  },

  // Guest Management
  getGuests: (params) => {
    return hmsApi.get('/provider/v1/guest', { params });
  },

  // Booking Notifications
  getBookingNotifications: () => {
    return hmsApi.get('/provider/v1/booking/notification');
  },

  markBookingNotificationAsRead: (objectId) => {
    return hmsApi.put(`/provider/v1/booking/notification/read/${objectId}`);
  },

  getGuestById: (guestId) => {
    return hmsApi.get(`/provider/v1/guest/${guestId}`);
  },

  getAvailableRoomNumbers: (objectId) => {
    return hmsApi.get(`/provider/v1/available/room/number/${objectId}`);
  },

  addGuest: (payload) => {
    return hmsApi.post('/provider/v1/guest', payload, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  },

  onboardGuest: (payload) => {
    return hmsApi.post('/provider/v1/onboard/guest', payload, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  },

  updateGuest: (guestId, payload) => {
    return hmsApi.put(`/provider/v1/guest/${guestId}`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // Staff Management
  getStaff: (objectId) => {
    return hmsApi.get(`/provider/v1/staff/${objectId}`);
  },

  getStaffSalaryPayment: (objectId) => {
    return hmsApi.get(`/provider/v1/staff/salary/payment/${objectId}`);
  },

  registerStaff: (objectId, payload) => {
    return hmsApi.post(`/provider/v1/register/staff/${objectId}`, payload, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  },

  updateStaffMember: (staffId, objectId, payload) => {
    return hmsApi.put(`/provider/v1/staff/member/${staffId}`, payload, {
      params: { objectId },
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  },

  deleteStaffMember: (staffId, objectId) => {
    return hmsApi.delete(`/provider/v1/staff/member/${staffId}`, {
      params: { objectId }
    });
  },

  // Media & Video
  getVideoProcessing: (objectId, params) => {
    return hmsApi.get(`/provider/v1/video/processing/${objectId}`, { params });
  },

  syncVideo: (processingId) => {
    return hmsApi.post(`/provider/v1/video/sync/${processingId}`);
  },

  getUploadVideoApi: (params) => {
    return hmsApi.get('/provider/v1/upload/video/APIVIDEO', { params });
  },

  // Dashboard
  getObjectDashboard: (objectId) => {
    return hmsApi.get(`/provider/v1/dashboard/object/${objectId}`);
  },

  // Outing attendance (guest check-out / check-in outside premises)
  getOutingAttendance: (objectId, params = {}) => {
    return hmsApi.get(`/provider/v1/attendance/${objectId}`, { params });
  },

  // Maintenance/Complaints
  getComplaints: (objectId) => {
    return hmsApi.get(`/provider/v1/complain/${objectId}`);
  },

  getComplaintById: (complainId, objectId, guestId) => {
    return hmsApi.get(`/provider/v1/complain/${complainId}`, {
      params: { objectId, guestId }
    });
  },

  createComplaint: (guestId, payload) => {
    return hmsApi.post(`/provider/v1/complain/${guestId}`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  updateComplaint: (complainId, objectId, guestId, payload) => {
    return hmsApi.put(`/provider/v1/complain/${complainId}`, payload, {
      params: { objectId, guestId },
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // Food Menu
  getFoodMenu: (objectId) => {
    return hmsApi.get(`/provider/v1/foodmenu/${objectId}`);
  },

  createFoodMenu: (objectId, payload) => {
    return hmsApi.post(`/provider/v1/foodmenu/${objectId}`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  updateFoodMenu: (menuId, objectId, payload) => {
    return hmsApi.put(`/provider/v1/foodmenu/${menuId}`, payload, {
      params: { objectId },
      headers: { 'Content-Type': 'application/json' }
    });
  },

  deleteFoodMenu: (menuId, objectId) => {
    return hmsApi.delete(`/provider/v1/foodmenu/${menuId}`, {
      params: { objectId }
    });
  },

  // Kitchen Stock Management
  // =========================

  // 1) List kitchen items
  getKitchenItems: (objectId, page = 1, pagesize = 15) => {
    return hmsApi.get(`/provider/v1/kitchen/items/${objectId}`, {
      params: { page, pagesize }
    });
  },

  // 2) Add kitchen item
  addKitchenItem: (objectId, payload) => {
    const allowedUnits = ['kg', 'litre', 'pcs'];
    const rawUnit = payload?.unit;
    const unit = allowedUnits.includes(rawUnit) ? rawUnit : null;

    if (!unit) {
      throw new Error(`Invalid kitchen unit. Allowed: ${allowedUnits.join(', ')}`);
    }

    const body = {
      name: payload?.name,
      unit,
      // Add-item UI can omit starting quantity, so default to 0.
      current_quantity: Number(payload?.current_quantity ?? 0),
      low_stock_threshold: Number(payload?.low_stock_threshold)
    };

    return hmsApi.post(`/provider/v1/kitchen/items/${objectId}`, body, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 2) Kitchen Stock Update (ADD / CONSUME)
  updateKitchenStock: (objectId, payload) => {
    const body = {
      objectid: payload?.objectid ?? objectId,
      itemid: payload?.itemid,
      type: payload?.type,
      quantity: Number(payload?.quantity),
      note: payload?.note ?? ''
    };

    return hmsApi.post(`/provider/v1/kitchen/stock/update/${objectId}`, body, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 3) Kitchen Item Threshold Update
  updateKitchenItemThreshold: (itemId, objectId, low_stock_threshold) => {
    return hmsApi.patch(
      `/provider/v1/kitchen/items/${itemId}/threshold/${objectId}`,
      { low_stock_threshold: Number(low_stock_threshold) },
      { headers: { 'Content-Type': 'application/json' } }
    );
  },

  // 4) Delete item
  deleteKitchenItem: (itemId, objectId) => {
    return hmsApi.delete(`/provider/v1/kitchen/items/${itemId}/${objectId}`);
  },

  // 6) List kitchen stock logs
  getKitchenStockLogs: (objectId) => {
    return hmsApi.get(`/provider/v1/kitchen/stock/logs/${objectId}`);
  },

  // Rent Payment
  getRentPayments: (params) => {
    return hmsApi.get('/provider/v1/rent/payment', { params });
  },

  updateRentPayment: (rentpaymentid, payload) => {
    return hmsApi.put(`/provider/v1/rent/payment/${rentpaymentid}`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // ============ Upload API Endpoints ============

  // Image Upload
  uploadImage: (formData) => {
    return uploadApi.post('/product/image/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Export both the service and the axios instances for direct use if needed
export default apiService;
export { smsApiWithDeviceToken, smsApiWithSessionToken, hmsApi, uploadApi, epayApi, SMS_API_BASE, HMS_API_BASE, UPLOAD_API_BASE, EPAY_API_BASE };

